import {FC, useCallback, useMemo, useState} from "react";
import {Box, LinearProgress, useTheme} from "@mui/material";
import {DataGridPro, GridRowParams, GridSelectionModel, useGridApiRef} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useUsersListGrid} from "./hooks/useUsersListGrid";
import {useNavigate} from "react-router-dom";
import type {UsersListGridRow} from "./getUsersListHeadCells";
import type {GetCohortSettingsResponse, UserListDto} from "@/app2/shared/data/gen";
import {
	DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
	PAGE_SIZE_OPTIONS_STANDARD_ADMIN
} from "@/app2/shared/data";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {LocalizationProvider} from "@mui/x-date-pickers";
import * as pkg from "@mui/x-date-pickers/AdapterDayjs/index.js";
import {AdminTableColumnsPanel} from "@/components/common/table/AdminTableColumnsPanel";
import {getUsersListHeadCells} from "./getUsersListHeadCells";
import {serverSideDateTimeFilterPanelProps} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";
import {DataGridSelectionCsvToolbar} from "@/app2/shared/components/DataGridSelectionCsvToolbar";
const {AdapterDayjs} = pkg;

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		padding: "15px",
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	}
}));

interface UsersListGridProps {
	/** Cohort settings for custom field column visibility and labels (same as Team Progress). */
	organization?: GetCohortSettingsResponse | null;
}

export const UsersListGrid: FC<UsersListGridProps> = ({organization}) => {
	const apiRef = useGridApiRef();
	const {classes} = useStyles();
	const navigate = useNavigate();
	const defaultFilterSettings = {
		page: 1,
		pageSize: DEFAULT_STANDARD_ADMIN_LIST_PAGE_SIZE,
		sortBy: "lastname",
		sortDirection: "asc"
	};
	const {
		const: {filterModel},
		api: {
			assignFilters,
			getPersistedFilterModel,
			handleFilterModelChange
		}} = useCustomDataGrid<UsersListGridRow>({
		defaultFilterSettings,
		filterStorageKey: "usersFilterSettings"
	});
	const initialFilterSettings = getPersistedFilterModel();
	const {
		rows,
		rowCount,
		isLoading,
		isFetched,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange
	} = useUsersListGrid({initialFilterSettings, onFilterModelChange: handleFilterModelChange});
	const data = useMemo(() =>
		isFetched && !isLoading ? rows : [],
	[isFetched, isLoading, rows]);
	const [selectedUsers, setSelectedUsers] = useState<UserListDto[]>([]);

	const theme = useTheme();
	const columns = getUsersListHeadCells(theme, organization)

	const handleRowClick = (params: GridRowParams<UsersListGridRow>) => {
		navigate(`/admin/users/${params.row.id}`);
	};

	const handleSelectionModelChange = (selectionModel: GridSelectionModel) => {
		const existingSelectedUsers = selectedUsers.filter((user) => selectionModel.includes(user.id));
		const existingUserIds = new Set(existingSelectedUsers.map((user) => user.id));
		const newGridRowIds = selectionModel.filter((id) => !existingUserIds.has(id as number));
		const newSelectedUsers = newGridRowIds
			.map((id) => data.find((row) => row.id === id))
			.filter((u): u is UserListDto => u != null);
		setSelectedUsers([...existingSelectedUsers, ...newSelectedUsers]);
	};

	const Toolbar = useCallback(
		() => <DataGridSelectionCsvToolbar selectedRows={selectedUsers} />,
		[selectedUsers]
	);

	return (
		<Box>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DataGridPro<UsersListGridRow>
					apiRef={apiRef}
					autoHeight
					className={classes.dataGrid}
					loading={isLoading}
					sortingMode="server"
					keepNonExistentRowsSelected
					page={page}
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					onPageChange={onPageChange}
					checkboxSelection
					paginationMode="server"
					pagination
					rowCount={rowCount}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					rows={data}
					columns={columns.map(e => assignFilters(e as any))}
					filterMode="server"
					filterModel={filterModel}
					onFilterModelChange={onFilterChange}
					onSelectionModelChange={handleSelectionModelChange}
					components={{Toolbar, ColumnsPanel: AdminTableColumnsPanel}}
					componentsProps={{
						filterPanel: serverSideDateTimeFilterPanelProps
					}}
					rowsPerPageOptions={[...PAGE_SIZE_OPTIONS_STANDARD_ADMIN]}
					initialState={{
						columns: {
							columnVisibilityModel: {
								createdAt: false,
								phone: false,
								workload: false,
								email: false,
								jobTitle: false,
								customFieldValue1: false,
								customFieldValue2: false,
								customFieldValue3: false,
								employeeCode: false,
								hireDate: false,
								titleStartDate: false

							}
						}
					}}
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					sx={{
						"& .MuiDataGrid-row": {
							cursor: "pointer"
						}
					}}
				/>
			</LocalizationProvider>
			{isLoading && <LinearProgress />}
		</Box>
	);
};
